import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { I } from '../../../../common/v5/config';
import {
  normalizeSidebarFolderState,
  generateSidebarFolderId,
  toolStableStorageKey,
} from '../utils/sidebarFolderStorage';

const SidebarContainer = styled.div`
  width: ${props => props.collapsed ? '60px' : '320px'};
  height: 100%;
  background-color: var(--cention-color-background-light);
  border-right: var(--cention-border-width-base) solid var(--cention-color-border-base);
  display: flex;
  flex-direction: column;
  transition: width 150ms ease-in-out;
  z-index: 50;
  position: relative;
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--cention-spacing-s) var(--cention-spacing-m);
  border-bottom: var(--cention-border-width-base) solid var(--cention-color-border-base);
  background-color: var(--cention-color-background-xlight);
  height: 60px;
  box-sizing: border-box;
`;

const SidebarTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: var(--cention-color-text-dark);
  margin: 0;
  opacity: ${props => props.collapsed ? 0 : 1};
  transition: opacity 150ms ease-in-out;
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  padding: var(--cention-spacing-xs);
  border-radius: var(--cention-border-radius-small);
  cursor: pointer;
  color: var(--cention-color-text-base);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  
  &:hover {
    background-color: var(--cention-color-background-base);
  }
  
  i {
    font-size: 14px;
    transition: transform 150ms ease-in-out;
    transform: ${props => props.collapsed ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const SearchContainer = styled.div`
  padding: var(--cention-spacing-m);
  opacity: ${props => props.collapsed ? 0 : 1};
  transition: opacity 150ms ease-in-out;
  pointer-events: ${props => props.collapsed ? 'none' : 'auto'};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: var(--cention-spacing-s) var(--cention-spacing-s) var(--cention-spacing-s) 36px;
  border: var(--cention-border-width-base) solid var(--cention-color-border-base);
  border-radius: var(--cention-border-radius-base);
  font-size: 13px;
  background-color: white;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="%23666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>');
  background-repeat: no-repeat;
  background-position: 12px center;
  background-size: 16px;
  
  &:focus {
    outline: none;
    border-color: var(--cention-color-primary);
    box-shadow: 0 0 0 2px var(--cention-color-primary-tint-2);
  }
  
  &::placeholder {
    color: var(--cention-color-text-light);
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: var(--cention-spacing-s);
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--cention-color-border-base);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: var(--cention-color-text-lighter);
  }
`;

const NodeSection = styled.div`
  margin-bottom: var(--cention-spacing-m);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--cention-spacing-s) var(--cention-spacing-m);
  font-size: 12px;
  font-weight: 600;
  color: var(--cention-color-text-light);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: var(--cention-color-background-base);
  border-bottom: var(--cention-border-width-base) solid var(--cention-color-border-base);
  opacity: ${props => props.collapsed ? 0 : 1};
  transition: opacity 150ms ease-in-out;
`;

const AddButton = styled.button`
  background: none;
  border: none;
  color: var(--cention-color-text-base);
  cursor: pointer;
  padding: 2px 4px;
  border-radius: var(--cention-border-radius-small);
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  transition: all 150ms ease-in-out;
  opacity: ${props => props.collapsed ? 0 : 0.7};
  pointer-events: ${props => props.collapsed ? 'none' : 'auto'};

  &:hover {
    background-color: var(--cention-color-background-light);
    color: var(--cention-color-primary);
    opacity: 1;
  }

  &:active {
    background-color: var(--cention-color-background-dark);
  }

  i {
    font-size: 12px;
  }
`;

const NodeList = styled.div`
  padding: var(--cention-spacing-xs) 0;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--cention-color-border-base);
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: var(--cention-color-text-lighter);
  }
  
  /* Fade effect at bottom when scrollable */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: linear-gradient(transparent, var(--cention-color-background-light));
    pointer-events: none;
    opacity: ${props => props.hasScroll ? 1 : 0};
    transition: opacity 150ms ease-in-out;
  }
`;

const NodeItem = styled.div`
  display: flex;
  align-items: center;
  padding: var(--cention-spacing-s) ${props => props.collapsed ? 'var(--cention-spacing-s)' : 'var(--cention-spacing-m)'};
  cursor: pointer;
  transition: all 150ms ease-in-out;
  border-radius: 0;
  position: relative;
  justify-content: ${props => props.collapsed ? 'center' : 'flex-start'};
  
  &:hover {
    background-color: var(--cention-color-background-base);
  }
  
  &:active {
    background-color: var(--cention-color-background-dark);
  }
`;

const NodeIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: var(--cention-border-radius-base);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  margin-right: ${props => props.collapsed ? '0' : 'var(--cention-spacing-s)'};
  background: ${props => {
    switch(props.type) {
      case 'agent': return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'tool': return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
      case 'model': return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
      case 'memory': return 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)';
      default: return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }
  }};
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const NodeInfo = styled.div`
  flex: 1;
  min-width: 0;
  opacity: ${props => props.collapsed ? 0 : 1};
  transition: opacity 150ms ease-in-out;
  pointer-events: ${props => props.collapsed ? 'none' : 'auto'};
`;

const NodeTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: var(--cention-color-text-dark);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`;

const NodeDescription = styled.div`
  font-size: 11px;
  color: var(--cention-color-text-light);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
  line-height: 1.2;
`;

const EmptyState = styled.div`
  padding: var(--cention-spacing-l);
  text-align: center;
  color: var(--cention-color-text-light);
  font-size: 12px;
  opacity: ${props => props.collapsed ? 0 : 1};
  transition: opacity 150ms ease-in-out;
  
  i {
    font-size: 24px;
    margin-bottom: var(--cention-spacing-s);
    display: block;
    color: var(--cention-color-text-lighter);
  }
`;

const FolderGroupHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 6px var(--cention-spacing-m) 6px 10px;
  font-size: 11px;
  font-weight: 600;
  color: var(--cention-color-text-light);
  letter-spacing: 0.3px;
  cursor: pointer;
  user-select: none;
  border-top: 1px solid var(--cention-color-border-base);
  &:hover {
    background-color: var(--cention-color-background-base);
  }
`;

const FolderChevron = styled.span`
  display: inline-block;
  width: 14px;
  margin-right: 6px;
  font-size: 10px;
  transition: transform 120ms ease;
  transform: rotate(${props => (props.open ? '90deg' : '0deg')});
`;

const FolderAccentBtn = styled.button`
  background: none;
  border: none;
  color: var(--cention-color-text-base);
  cursor: pointer;
  padding: 2px 4px;
  margin-left: 6px;
  border-radius: var(--cention-border-radius-small);
  opacity: 0.75;
  &:hover {
    opacity: 1;
    color: var(--cention-color-primary);
    background-color: var(--cention-color-background-light);
  }
  i {
    font-size: 11px;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: var(--cention-spacing-s);
  background: var(--cention-color-text-dark);
  color: white;
  padding: var(--cention-spacing-2xs) var(--cention-spacing-xs);
  border-radius: var(--cention-border-radius-small);
  font-size: 11px;
  white-space: nowrap;
  z-index: 1000;
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms ease-in-out;
  
  &::before {
    content: '';
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 4px solid transparent;
    border-right-color: var(--cention-color-text-dark);
  }
  
  ${NodeItem}:hover & {
    opacity: ${props => props.collapsed ? 1 : 0};
  }
`;

const ContextMenu = styled.div`
  background: #2a2a2a;
  border: 1px solid #404040;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  min-width: 120px;
  overflow: hidden;
  position: fixed;
  z-index: 9999;
  pointer-events: auto;
`;

const ContextMenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  font-size: 12px;
  color: #e0e0e0;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: #404040;
  }
  
  &.destructive {
    color: #ff6b6b;
    
    &:hover {
      background: rgba(255, 107, 107, 0.1);
    }
  }
  
  i {
    margin-right: 8px;
    width: 12px;
    text-align: center;
  }
`;
const getNodeIcon = (type) => {
  switch (type) {
    case 'agent':
      return '🤖';
    case 'tool':
      return '🔧';
    default:
      return '📦';
  }
};

const CentionSidebar = ({
  collapsed = false,
  onToggleCollapse,
  agents = [],
  tools = [],
  sidebarLayout,
  onSidebarLayoutChange,
  onPatchAgentFolder,
  onNodeClick,
  onCreateAgent,
  onCreateTool,
  onEditAgent,
  onDeleteAgent,
  onEditTool,
  onDeleteTool,
  isDeletingAgent = false,
  isDeletingTool = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [collapsedFolderKeys, setCollapsedFolderKeys] = useState({});

  const folderState = useMemo(() => normalizeSidebarFolderState(sidebarLayout), [sidebarLayout]);

  const filterItems = (items) => {
    if (!searchTerm) return items;
    const searchLower = searchTerm.toLowerCase();
    return items.filter(
      (item) =>
        (item.name || item.title || item.agent_name || '').toLowerCase().includes(searchLower) ||
        (item.description || '').toLowerCase().includes(searchLower)
    );
  };

  const folderKey = (kind, id) => `${kind}:${id}`;
  const isFolderOpen = (kind, id) => !collapsedFolderKeys[folderKey(kind, id)];
  const toggleFolderOpen = (kind, id) => {
    const k = folderKey(kind, id);
    setCollapsedFolderKeys((prev) => {
      const next = { ...prev };
      if (next[k]) delete next[k];
      else next[k] = true;
      return next;
    });
  };

  const addFolder = (kind) => {
    const label = window.prompt(I('Folder name'));
    if (!label || !String(label).trim()) return;
    const row = { id: generateSidebarFolderId(), name: String(label).trim() };
    const prev = folderState;
    onSidebarLayoutChange?.({
      ...prev,
      agentFolders: kind === 'agent' ? [...prev.agentFolders, row] : prev.agentFolders,
      toolFolders: kind === 'tool' ? [...prev.toolFolders, row] : prev.toolFolders,
    });
  };

  const deleteFolderById = async (kind, folderId) => {
    if (!window.confirm(I('Delete this folder? Items will move to Unfiled.'))) return;
    if (kind === 'agent' && onPatchAgentFolder) {
      const toClear = agents.filter((a) => a.builder_folder_id === folderId);
      await Promise.all(toClear.map((a) => onPatchAgentFolder(a.id, '')));
    }
    const prev = folderState;
    const next = {
      ...prev,
      toolAssignments: { ...prev.toolAssignments },
    };
    if (kind === 'agent') {
      next.agentFolders = prev.agentFolders.filter((f) => f.id !== folderId);
    } else {
      next.toolFolders = prev.toolFolders.filter((f) => f.id !== folderId);
      Object.keys(next.toolAssignments).forEach((k) => {
        if (next.toolAssignments[k] === folderId) delete next.toolAssignments[k];
      });
    }
    onSidebarLayoutChange?.(next);
  };

  const placeContextMenu = (e, approxHeight = 120) => {
    const menuWidth = 200;
    const menuHeight = approxHeight;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    let x = e.clientX;
    let y = e.clientY;
    if (x + menuWidth > viewportWidth) {
      x = viewportWidth - menuWidth - 10;
    }
    if (y + menuHeight > viewportHeight) {
      y = y - menuHeight;
    }
    x = Math.max(10, x);
    y = Math.max(10, y);
    return { x, y };
  };

  const handleNodeClick = (node, type) => {
    onNodeClick?.(node, type);
  };

  const handleContextMenu = (e, item, type) => {
    e.preventDefault();
    e.stopPropagation();
    const { x, y } = placeContextMenu(e, 160);
    setContextMenu({
      x,
      y,
      menuKind: 'main',
      item,
      type,
    });
  };

  const handleFolderRowContextMenu = (e, folder, kind) => {
    e.preventDefault();
    e.stopPropagation();
    const { x, y } = placeContextMenu(e, 80);
    setContextMenu({
      x,
      y,
      menuKind: 'folderHeader',
      folder,
      folderKind: kind,
    });
  };

  const applyFolderPick = (folderId) => {
    if (!contextMenu || contextMenu.menuKind !== 'pickFolder') return;
    const { item, type } = contextMenu;
    if (type === 'agent' && onPatchAgentFolder) {
      onPatchAgentFolder(item.id || item.agent_id, folderId);
    } else if (type === 'tool') {
      const key = toolStableStorageKey(item);
      const prev = folderState;
      const nextTa = { ...prev.toolAssignments };
      if (!folderId) delete nextTa[key];
      else nextTa[key] = folderId;
      onSidebarLayoutChange?.({ ...prev, toolAssignments: nextTa });
    }
    setContextMenu(null);
  };

  const handleContextMenuAction = (action, item, type) => {
    setContextMenu(null);

    switch (action) {
      case 'edit':
        if (type === 'agent' && onEditAgent && !isDeletingAgent) {
          onEditAgent(item);
        } else if (type === 'tool' && onEditTool && !isDeletingTool) {
          onEditTool(item);
        }
        break;
      case 'delete':
        if (type === 'agent' && onDeleteAgent && !isDeletingAgent) {
          if (window.confirm(`Are you sure you want to delete ${item.name || item.title}?`)) {
            onDeleteAgent(item.agent_id || item.id);
          }
        } else if (type === 'tool' && onDeleteTool && !isDeletingTool) {
          if (window.confirm(`Are you sure you want to delete ${item.name || item.title}?`)) {
            onDeleteTool(item.tool_id || item.id);
          }
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(null);
    };

    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  const renderNodeRow = (item, type, index) => (
    <NodeItem
      key={item.id || item.agent_id || item.tool_id || index}
      collapsed={collapsed}
      onClick={() => handleNodeClick(item, type)}
      onContextMenu={(e) => handleContextMenu(e, item, type)}
      title={collapsed ? item.name || item.title || item.agent_name || item.agent_id : ''}
    >
      <NodeIcon type={type} collapsed={collapsed}>
        {getNodeIcon(type)}
      </NodeIcon>
      <NodeInfo collapsed={collapsed}>
        <NodeTitle>{item.name || item.title || item.agent_name || item.agent_id}</NodeTitle>
        {item.description && <NodeDescription>{item.description}</NodeDescription>}
      </NodeInfo>
      {collapsed && (
        <Tooltip collapsed={collapsed}>
          {item.name || item.title || item.agent_name || item.agent_id}
          {item.description && (
            <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.8 }}>{item.description}</div>
          )}
        </Tooltip>
      )}
    </NodeItem>
  );

  const renderFlatNodeSection = (title, items, type) => {
    const filteredItems = filterItems(items);
    const hasScroll = filteredItems.length > 8;

    return (
      <NodeSection key={type}>
        <SectionHeader collapsed={collapsed}>
          <span>{title}</span>
          {type === 'agent' && onCreateAgent && (
            <AddButton
              collapsed={collapsed}
              onClick={(e) => {
                e.stopPropagation();
                onCreateAgent();
              }}
              title={I('Create new agent')}
            >
              <i className="fa fa-plus" />
            </AddButton>
          )}
          {type === 'tool' && onCreateTool && (
            <AddButton
              collapsed={collapsed}
              onClick={(e) => {
                e.stopPropagation();
                onCreateTool();
              }}
              title={I('Create new tool')}
            >
              <i className="fa fa-plus" />
            </AddButton>
          )}
        </SectionHeader>
        <NodeList hasScroll={hasScroll}>
          {filteredItems.length === 0 ? (
            <EmptyState collapsed={collapsed}>
              <i className="fa fa-inbox" />
              {I(`No ${type}s available`)}
            </EmptyState>
          ) : (
            filteredItems.map((item, index) => renderNodeRow(item, type, index))
          )}
        </NodeList>
      </NodeSection>
    );
  };

  const renderOrganizedSection = (title, items, type, folders, assignments) => {
    const filteredItems = filterItems(items);
    const sortedFolders = [...folders].sort((a, b) => a.name.localeCompare(b.name));
    const folderIdSet = new Set(sortedFolders.map((f) => f.id));

    const toolFolderOf = (tool) => assignments[toolStableStorageKey(tool)] || '';

    const unfiled = filteredItems.filter((it) => {
      if (type === 'agent') {
        const fid = it.builder_folder_id;
        return !fid || !folderIdSet.has(fid);
      }
      const fid = toolFolderOf(it);
      return !fid || !folderIdSet.has(fid);
    });

    const rowsForFolder = (folderId) =>
      filteredItems.filter((it) => {
        if (type === 'agent') return it.builder_folder_id === folderId;
        return toolFolderOf(it) === folderId;
      });

    const hasScroll = filteredItems.length > 8;

    return (
      <NodeSection key={type}>
        <SectionHeader collapsed={collapsed}>
          <span>{title}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FolderAccentBtn
              type="button"
              title={I('New folder')}
              onClick={(e) => {
                e.stopPropagation();
                addFolder(type);
              }}
            >
              <i className="fa fa-folder-o" />
            </FolderAccentBtn>
            {type === 'agent' && onCreateAgent && (
              <AddButton
                collapsed={collapsed}
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateAgent();
                }}
                title={I('Create new agent')}
              >
                <i className="fa fa-plus" />
              </AddButton>
            )}
            {type === 'tool' && onCreateTool && (
              <AddButton
                collapsed={collapsed}
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateTool();
                }}
                title={I('Create new tool')}
              >
                <i className="fa fa-plus" />
              </AddButton>
            )}
          </span>
        </SectionHeader>
        <NodeList hasScroll={hasScroll}>
          {filteredItems.length === 0 ? (
            <EmptyState collapsed={collapsed}>
              <i className="fa fa-inbox" />
              {I(`No ${type}s available`)}
            </EmptyState>
          ) : (
            <>
              {sortedFolders.map((folder) => {
                const open = isFolderOpen(type, folder.id);
                const inner = rowsForFolder(folder.id);
                return (
                  <div key={folder.id}>
                    <FolderGroupHeader
                      onClick={() => toggleFolderOpen(type, folder.id)}
                      onContextMenu={(e) => handleFolderRowContextMenu(e, folder, type)}
                    >
                      <FolderChevron open={open}>▸</FolderChevron>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        📁 {folder.name}{' '}
                        <span style={{ opacity: 0.7, fontWeight: 500 }}>({inner.length})</span>
                      </span>
                    </FolderGroupHeader>
                    {open && inner.map((item, index) => renderNodeRow(item, type, index))}
                  </div>
                );
              })}
              <FolderGroupHeader onClick={() => toggleFolderOpen(type, '_unfiled')}>
                <FolderChevron open={isFolderOpen(type, '_unfiled')}>▸</FolderChevron>
                <span style={{ flex: 1 }}>{I('Unfiled')}</span>
                <span style={{ opacity: 0.7 }}>({unfiled.length})</span>
              </FolderGroupHeader>
              {isFolderOpen(type, '_unfiled') && unfiled.map((item, index) => renderNodeRow(item, type, index))}
            </>
          )}
        </NodeList>
      </NodeSection>
    );
  };

  return (
    <>
      {contextMenu && (
        <ContextMenu
          style={{
            top: contextMenu.y,
            left: contextMenu.x,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.menuKind === 'folderHeader' && (
            <ContextMenuItem
              className="destructive"
              onClick={() => {
                const { folder, folderKind } = contextMenu;
                setContextMenu(null);
                if (folder && folderKind) {
                  deleteFolderById(folderKind, folder.id);
                }
              }}
            >
              <i className="fa fa-trash" />
              {I('Delete folder')}
            </ContextMenuItem>
          )}
          {contextMenu.menuKind === 'pickFolder' && (
            <>
              <ContextMenuItem
                onClick={() => setContextMenu((prev) => (prev ? { ...prev, menuKind: 'main' } : null))}
              >
                <i className="fa fa-arrow-left" />
                {I('Back')}
              </ContextMenuItem>
              <ContextMenuItem onClick={() => applyFolderPick('')}>
                <i className="fa fa-inbox" />
                {I('Unfiled')}
              </ContextMenuItem>
              {(contextMenu.type === 'agent' ? folderState.agentFolders : folderState.toolFolders)
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((f) => (
                  <ContextMenuItem key={f.id} onClick={() => applyFolderPick(f.id)}>
                    <i className="fa fa-folder" />
                    {f.name}
                  </ContextMenuItem>
                ))}
            </>
          )}
          {contextMenu.menuKind === 'main' && contextMenu.item && (
            <>
              <ContextMenuItem
                onClick={() => handleContextMenuAction('edit', contextMenu.item, contextMenu.type)}
                style={{
                  opacity:
                    (contextMenu.type === 'agent' && isDeletingAgent) ||
                    (contextMenu.type === 'tool' && isDeletingTool)
                      ? 0.5
                      : 1,
                  cursor:
                    (contextMenu.type === 'agent' && isDeletingAgent) ||
                    (contextMenu.type === 'tool' && isDeletingTool)
                      ? 'not-allowed'
                      : 'pointer',
                }}
              >
                <i className="fa fa-edit" />
                {I('Edit')}
              </ContextMenuItem>
              <ContextMenuItem
                onClick={() => handleContextMenuAction('delete', contextMenu.item, contextMenu.type)}
                className="destructive"
                style={{
                  opacity:
                    (contextMenu.type === 'agent' && isDeletingAgent) ||
                    (contextMenu.type === 'tool' && isDeletingTool)
                      ? 0.5
                      : 1,
                  cursor:
                    (contextMenu.type === 'agent' && isDeletingAgent) ||
                    (contextMenu.type === 'tool' && isDeletingTool)
                      ? 'not-allowed'
                      : 'pointer',
                }}
              >
                <i className="fa fa-trash" />
                {(contextMenu.type === 'agent' && isDeletingAgent) || (contextMenu.type === 'tool' && isDeletingTool)
                  ? I('Deleting...')
                  : I('Delete')}
              </ContextMenuItem>
              {!collapsed &&
                ((contextMenu.type === 'agent' && onPatchAgentFolder) || contextMenu.type === 'tool') && (
                  <ContextMenuItem
                    onClick={() => setContextMenu((prev) => (prev ? { ...prev, menuKind: 'pickFolder' } : null))}
                  >
                    <i className="fa fa-folder-o" />
                    {I('Move to folder…')}
                  </ContextMenuItem>
                )}
            </>
          )}
        </ContextMenu>
      )}
      <SidebarContainer collapsed={collapsed}>
        <SidebarHeader>
          <SidebarTitle collapsed={collapsed}>{I('Node Creator')}</SidebarTitle>
          <CollapseButton
            collapsed={collapsed}
            onClick={onToggleCollapse}
            title={collapsed ? I('Expand sidebar') : I('Collapse sidebar')}
          >
            <i className="fa fa-chevron-left" />
          </CollapseButton>
        </SidebarHeader>

        {!collapsed && (
          <SearchContainer collapsed={collapsed}>
            <SearchInput
              type="text"
              placeholder={I('Search nodes...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>
        )}

        <SidebarContent>
          {collapsed ? (
            <>
              {renderFlatNodeSection(I('Agents'), agents, 'agent')}
              {renderFlatNodeSection(I('Tools'), tools, 'tool')}
            </>
          ) : (
            <>
              {renderOrganizedSection(I('Agents'), agents, 'agent', folderState.agentFolders, {})}
              {renderOrganizedSection(
                I('Tools'),
                tools,
                'tool',
                folderState.toolFolders,
                folderState.toolAssignments
              )}
            </>
          )}
        </SidebarContent>
      </SidebarContainer>
    </>
  );
};

export default CentionSidebar;
